'use strict';

import Cookie from 'js-cookie';

/**
 * @typedef {Object} UserData
 * @property {string} name - User's first name
 * @property {string} email - User's email address
 * @property {string|undefined} hashedEmail - SHA-256 hashed email address
 */

/**
 * @typedef {Object} UserState - Stored in cookie and window.user
 * @property {boolean} loggedIn
 * @property {UserData|null} data
 */

/**
 * Handles user state initialization
 */
function fetchUser() {
	const loggedInCookie = Cookie.get('loggedIn');
	const userCookie = Cookie.get('user');

	window.user = (loggedInCookie && userCookie)
		? JSON.parse(userCookie)
		: { loggedIn: false, data: null };

	if(!window.user.loggedIn) {
		fetch('/stage-ajax/getUser', {
			method: 'GET',
			cache: 'no-cache',
			headers: {
				'Content-Type': 'application/json'
			}
		}).then(response => {
			if (!response.ok) throw new Error('Network response error.');
			return response.json();
		}).then(async (data) => {
			// Store user data into window object
			await setUserData(data.user);
		}).catch((reason) => {
			console.warn('Failed to fetch user', reason);
		}).finally(() => {
			setUserCookies(window.user);
			waitForDom(updateDom, window.user?.data);
		});
	} else { // user is already logged in
		waitForDom(updateDom, window.user.data);
	}
}

/**
 * Saves or clears user data in window object
 * Data includes a SHA256 hashed email for Criteo analytics
 * @param {UserData?} [userData]
 */
async function setUserData(userData) {
	const { email, name, lastName = '' } = userData ?? {};
	const loggedIn = (email && name) ? true : false;
	window.user = {
		loggedIn,
		data: loggedIn ? {
			email,
			name,
			lastName,
			hashedEmail: await hashEmail(email)
		} : null
	};
}

/**
 * Saves or clears user data and login status in cookies
 * @param {UserState?} [user]
 */
function setUserCookies(user) {
	if (user?.loggedIn) {
		Cookie.set('user', JSON.stringify(user), { expires: 1, path: '/' });
		Cookie.set('loggedIn', 'true');
	} else {
		Cookie.remove('user');
		Cookie.remove('loggedIn');
	}
}

/**
 * Updates DOM to match current login state
 * @param {UserData?} [userData]
 */
function updateDom(userData) {
	const { email, name } = userData ?? {};
	updateUserName(name);
	showUserElements();
	setLoggedInClasses(email && name);
}

/**
 * Triggers a function with given arguments shortly after document.body becomes
 * available, or immediately if document.body is available.
 * @param {function} callback - The function to call
 * @param {...any} args - Arguments for the callback function
 */
function waitForDom(callback, ...args) {
	if(document.body) {
		callback(...args);
	} else {
		let bodyLoadedInterval = setInterval(() => {
			if(document.body) {
				clearInterval(bodyLoadedInterval);
				callback(...args);
			}
		}, 50);
	}
}

/**
 * Called externally when user logs in via login form
 * @param {UserState} userData - Incoming user data object
 */
function userLogIn(user) {
	setUserData(user);
	updateDom(user);
}

/**
 * Truncates and inserts user's name into matching elements
 * @param {string} [userName]
 */
function updateUserName(userName = 'Club') {
	const name = userName.length > 15 ? userName.substring(0, 15) : userName;
	document.querySelectorAll('.js-user-firstname').forEach(element => element.innerHTML = name);
}

/**
 * Displays certain elements, such as login links
 */
function showUserElements() {
	document.querySelectorAll('.js-user-initially-hidden').forEach(element => {
		element.classList.remove('u-hidden', 'js-user-initially-hidden');
	});
}

/**
 * @param {boolean} loggedIn
 */
function setLoggedInClasses(loggedIn = false) {
	document.body.classList.toggle('u-user--logged-in', loggedIn);
	document.body.classList.toggle('u-user--not-logged-in', !loggedIn);
}

/**
 * Encodes a Buffer as a hexadecimal string
 * @param {Buffer} buffer
 * @returns {string}
 */
function bufferToHexString(buffer) {
	return Array.from(new Uint8Array(buffer))
		.map(b => b.toString(16).padStart(2, '0'))
		.join('');
}

/**
 * Hashes an email address using SHA-256
 * @param {string?} email
 * @returns {Promise<string|undefined>}
 */
async function hashEmail(email) {
	const isCryptoSupported = !!window.crypto?.subtle
	if (!isCryptoSupported || typeof email !== 'string') return;
	const formattedEmail = email.trim().toLowerCase();
	return await window.crypto.subtle.digest(
		'SHA-256', new TextEncoder().encode(formattedEmail)
	).then(bufferToHexString);
}

export { fetchUser, userLogIn };
